import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  H1,
  Flex,
  FlexItem,
  Button,
  Icon,
  TextInput,
  Popover,
  Loading,
  Notification,
} from "orcs-design-system";
import GroupPropType from "src/custom-prop-types/group";

import { MAX_TEAM_NAME_LENGTH } from "src/consts/team";
import useUserRole from "src/app/Restricted/useUserRole";
import { ADMIN_ROLE, POWER_ROLE } from "src/services/auth";
import {
  validateTeamName,
  sanitizeTeamName,
} from "src/util/teamNameValidation";

// TODO: Find out a comfortable input width

// eslint-disable-next-line react/display-name
const ErrorNotification = React.memo(({ error, onDismiss }) => {
  if (!error) {
    return null;
  }

  return (
    <Notification
      floating
      centered
      colour="danger"
      icon={["far", "exclamation-circle"]}
      closable
      onDismiss={onDismiss}
    >
      {error}
    </Notification>
  );
});

ErrorNotification.propTypes = {
  error: PropTypes.string,
  onDismiss: PropTypes.func,
};

// eslint-disable-next-line react/display-name
const TeamName = React.memo(({ isEditMode, name, color = "white" }) => {
  if (isEditMode) {
    return null;
  }

  return (
    <H1
      color={color}
      fontWeight="600"
      fontSize="2.4rem"
      className="guided-tour-team-details-team-name"
    >
      {name}
    </H1>
  );
});

TeamName.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
};

// eslint-disable-next-line react/display-name
const EditBtn = React.memo(({ isEditMode, onEdit, disabled, popoverText }) => {
  if (isEditMode || disabled) {
    return null;
  }

  return (
    <Popover
      text={popoverText}
      width="fit-content"
      tabIndex="-1"
      direction="top"
      mt="1px"
    >
      <Button
        dataTestId="edit-btn"
        iconOnly
        onClick={onEdit}
        height="22px"
        width="22px"
        borderRadius="22px"
        variant="borderGrey"
        bg="white"
        ariaLabel={popoverText}
      >
        <Icon icon={["fas", "pencil"]} color="greyDarkest" />
      </Button>
    </Popover>
  );
});

EditBtn.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  popoverText: PropTypes.string,
};

// eslint-disable-next-line react/display-name
const ConfirmAndCancelBtns = React.memo(
  ({ isEditMode, onSave, saveDisabled, onCancel, canCancel, isLoading }) => {
    if (!isEditMode) {
      return null;
    }

    return (
      <>
        <Button
          dataTestId="save-btn"
          iconOnly
          ariaLabel="Save team name"
          variant={saveDisabled || isLoading ? "disabled" : "successAlternate"}
          onClick={onSave}
          height="28px"
          width="28px"
          borderRadius="28px"
          disabled={saveDisabled || isLoading}
        >
          {isLoading ? <Loading /> : <Icon icon={["fas", "check"]} />}
        </Button>
        {canCancel && !isLoading && (
          <Button
            dataTestId="cancel-btn"
            small
            iconOnly
            ariaLabel="Cancel"
            variant="dangerAlternate"
            onClick={onCancel}
            height="28px"
            width="28px"
            borderRadius="28px"
          >
            <Icon icon={["fas", "times"]} />
          </Button>
        )}
      </>
    );
  }
);

ConfirmAndCancelBtns.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  canCancel: PropTypes.bool.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const TeamNameInput = ({
  isEditMode,
  value,
  onChange,
  onBlur,
  onSave,
  onCancel,
  isLoading,
}) => {
  const handleFocus = (e) => e.target.select(); // auto select text
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSave();
    }
    if (e.key === "Escape") {
      onCancel();
    }
  };

  if (!isEditMode) {
    return null;
  }

  return (
    <FlexItem width="250px">
      <TextInput
        id="team-name-edit"
        autoFocus
        fullWidth
        height="28px"
        data-testid="team-name-editor-input"
        ariaLabel="Team name"
        className="guided-tour-team-details-team-name"
        disabled={isLoading}
        maxLength={MAX_TEAM_NAME_LENGTH}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
    </FlexItem>
  );
};

TeamNameInput.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const TeamNameEditor = ({
  team,
  updateName,
  isEditMode = false,
  canCancel = true,
  autoExitEditMode = true,
  color = "white",
}) => {
  const { name } = team;

  const [editMode, setEditMode] = useState(isEditMode);
  const [isLoading, setLoading] = useState(false);
  const [newName, setNewName] = useState(name);
  const [error, setError] = useState(null);

  const userRole = useUserRole();

  const editDisabled = useMemo(
    () => userRole !== POWER_ROLE && userRole !== ADMIN_ROLE,
    [userRole]
  );

  useEffect(() => {
    setNewName(name);
  }, [name]);

  const onEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const onSave = useCallback(async () => {
    // Validate the original input first (before sanitization)
    // team does not have direct parent as an object returned from query,
    // so the duplicate group name check is done in the graphql resolver
    const validationResult = validateTeamName(newName);
    if (validationResult) {
      setError(validationResult);
      return;
    }

    // Clear any previous errors since validation passed
    setError(null);

    // Sanitize before saving to ensure clean data is sent to backend
    const sanitizedName = sanitizeTeamName(newName);
    const proposedName = sanitizedName;

    if (proposedName === name) {
      return;
    }

    try {
      setLoading(true);
      await updateName(proposedName);
      setError(null);
      // TODO: Update team name immediately?
      setLoading(false);
    } catch (err) {
      // Extract clean error message from Apollo error structure without prefix
      const errorMessage =
        err.graphQLErrors?.[0]?.message ||
        err.message ||
        err.networkError?.message ||
        err.toString();
      setError(errorMessage);
      setLoading(false);
    } finally {
      if (autoExitEditMode) {
        setEditMode(false);
      }
    }
  }, [newName, name, updateName, autoExitEditMode]);

  const onCancel = useCallback(() => {
    setNewName(name);
    setEditMode(false);
    setError(null);
  }, [name]);

  const onNameChange = useCallback((e) => {
    setNewName(e.target.value);
  }, []);

  const onNameBlur = useCallback(
    (e) => {
      const inputValue = e.target.value;

      // Validate on blur if name has changed
      // Use same validation as onSave (including duplicate checking)
      if (inputValue !== name) {
        const result = validateTeamName(inputValue);
        if (result) {
          setError(result);
        } else {
          setError(null);
        }
      } else {
        setError(null);
      }
    },
    [name]
  );

  const onDismiss = useCallback(() => {
    setError(null);
  }, []);

  const popoverText = !editDisabled
    ? "Change team name"
    : "Your current access level does not allow you to edit team names";

  return (
    <Flex alignItems="center" gap="s">
      <TeamName isEditMode={editMode} name={name} color={color} />
      <TeamNameInput
        isEditMode={editMode}
        isLoading={isLoading}
        value={newName}
        onChange={onNameChange}
        onBlur={onNameBlur}
        onSave={onSave}
        onCancel={onCancel}
      />
      <EditBtn
        isEditMode={editMode}
        onEdit={onEdit}
        disabled={editDisabled}
        popoverText={popoverText}
      />
      <ConfirmAndCancelBtns
        isEditMode={editMode}
        isLoading={isLoading}
        canCancel={canCancel}
        saveDisabled={newName === name}
        onSave={onSave}
        onCancel={onCancel}
      />
      <ErrorNotification error={error} onDismiss={onDismiss} />
    </Flex>
  );
};

TeamNameEditor.propTypes = {
  team: GroupPropType.isRequired,
  updateName: PropTypes.func,
  isEditMode: PropTypes.bool,
  canCancel: PropTypes.bool,
  autoExitEditMode: PropTypes.bool,
  color: PropTypes.string,
};

export default TeamNameEditor;
